import {AbortCategory, deleteById, fetchJson, postJsonWithBody, putJsonWithBody} from './fetcher';
import {
    AiAssistantConversation,
    AiAssistantConversationContext,
    ChatType,
    HibuAssistantMessage,
    Role,
} from '../assistant/common/types';

const CHAT_HISTORY_BASE = '/api/chat-history';
const CONVERSATIONS_ENDPOINT = `${CHAT_HISTORY_BASE}/conversations`;

export interface ChatConversationDto {
    conversationId: string;
    chatType: ChatType;
    searchResultsRequests: string | null;
    suggestions: string | null;
    summary: string | null;
    createdAt: string;
    totalDuplicatesCount: number;
    contextSettings: string | null;
}

export interface ChatMessageDto {
    id: number;
    text: string;
    role: Role;
    metadata: string | null;
    createdAt: string;
}

export interface CreateConversationRequest {
    conversationId: string;
    chatType: ChatType;
    searchResultsRequests: string;
    suggestions: string;
    summary: string;
    totalDuplicatesCount: number;
    contextSettings: string;
}

export interface AddMessageRequest {
    text: string;
    role: Role;
    metadata?: string;
}

function safeParseJson<T>(value: string | null | undefined, fallback: T): T {
    if (value == null || value.trim() === '') return fallback;
    try {
        return JSON.parse(value) as T;
    } catch {
        return fallback;
    }
}

function safeStringify(value: unknown, fallback = '[]'): string {
    try {
        const s = JSON.stringify(value);
        return s ?? fallback;
    } catch {
        return fallback;
    }
}

function normalizeConversationContext(
    context: Partial<AiAssistantConversationContext>,
): AiAssistantConversationContext {
    return {
        enableDocuments: context.enableDocuments ?? true,
        enableWebSearch: context.enableWebSearch ?? false,
        tags: context.tags ?? [],
    };
}

function toValidDate(s: string): Date | null {
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : new Date(t);
}

export class ChatHistoryService {
    static async getConversations(chatType?: ChatType): Promise<ChatConversationDto[]> {
        const url = chatType ? `${CONVERSATIONS_ENDPOINT}?chatType=${chatType}` : CONVERSATIONS_ENDPOINT;
        return fetchJson<ChatConversationDto[]>(url, 'GET', AbortCategory.DETAILS);
    }

    static async getConversation(conversationId: string): Promise<ChatConversationDto | null> {
        try {
            return await fetchJson<ChatConversationDto>(
                `${CONVERSATIONS_ENDPOINT}/${conversationId}`,
                'GET',
                AbortCategory.DETAILS,
            );
        } catch (error: any) {
            if (error?.detail?.errorType === 404 || error?.status === 404 || error?.response?.status === 404) {
                return null;
            }
            console.error(`Error fetching conversation ${conversationId}:`, error);
            throw error;
        }
    }

    static async getMessages(conversationId: string): Promise<ChatMessageDto[]> {
        return fetchJson<ChatMessageDto[]>(
            `${CONVERSATIONS_ENDPOINT}/${conversationId}/messages`,
            'GET',
            AbortCategory.DETAILS,
        );
    }

    static async createConversation(request: CreateConversationRequest): Promise<ChatConversationDto> {
        return postJsonWithBody<ChatConversationDto>(
            CONVERSATIONS_ENDPOINT,
            JSON.stringify(request),
            AbortCategory.UPDATE,
        );
    }

    static async updateConversation(
        conversationId: string,
        conversation: ChatConversationDto,
    ): Promise<ChatConversationDto> {
        return putJsonWithBody<ChatConversationDto>(
            `${CONVERSATIONS_ENDPOINT}/${conversationId}`,
            JSON.stringify(conversation),
            AbortCategory.UPDATE,
        );
    }

    static async deleteConversation(conversationId: string): Promise<void> {
        return deleteById<void>(CONVERSATIONS_ENDPOINT, conversationId, AbortCategory.UPDATE);
    }

    static async addMessage(conversationId: string, message: AddMessageRequest): Promise<ChatMessageDto> {
        return postJsonWithBody<ChatMessageDto>(
            `${CONVERSATIONS_ENDPOINT}/${conversationId}/messages`,
            JSON.stringify(message),
            AbortCategory.UPDATE,
        );
    }

    static toAiAssistantConversation(dto: ChatConversationDto): AiAssistantConversation {
        const created = toValidDate(dto.createdAt);
        return {
            id: dto.conversationId,
            type: dto.chatType,
            conversationMessages: [],
            searchResultsRequests: safeParseJson(dto.searchResultsRequests, []),
            suggestions: safeParseJson(dto.suggestions, [] as string[]),
            summary: dto.summary ?? '',
            created: created ?? new Date(),
            totalDuplicatesCount: dto.totalDuplicatesCount ?? 0,
            context: normalizeConversationContext(safeParseJson(dto.contextSettings, {})),
        };
    }

    static toConversationDto(conversation: AiAssistantConversation): ChatConversationDto {
        return {
            conversationId: conversation.id,
            chatType: conversation.type,
            searchResultsRequests: JSON.stringify(conversation.searchResultsRequests),
            suggestions: JSON.stringify(conversation.suggestions),
            summary: conversation.summary,
            contextSettings: JSON.stringify(conversation.context),
            totalDuplicatesCount: conversation.totalDuplicatesCount ?? 0,
            createdAt: conversation.created.toISOString(),
        };
    }

    static toAddMessageRequest(message: HibuAssistantMessage): AddMessageRequest {
        return {
            text: message.text,
            role: message.role,
            metadata: message.metadata !== undefined ? safeStringify(message.metadata, '{}') : undefined,
        };
    }

    static toHibuAssistantMessage(dto: ChatMessageDto): HibuAssistantMessage {
        return {
            text: dto.text,
            role: dto.role,
            metadata: safeParseJson(dto.metadata, undefined),
        };
    }
}
